import { useDriverManagement } from '@/api/useDriverManagement'
import { TemplatePreview } from '@/components/DriverManagement/Manage/TemplateManagement/TemplatePreview'
import { isDefined } from '@/utils/general'
import { DatePicker, Form, type FormInstance, type FormItemProps, Select } from 'antd'
import type { NamePath } from 'antd/es/form/interface'
import type { Dayjs } from 'dayjs'
import { useEffect, useMemo } from 'react'
import { DriversSelect } from '../Inputs/DriversSelect'

export type BulkAddTemplateForm = {
  drivers: string[]
  startDate: Dayjs | null
  endDate: Dayjs | null
  templateId?: string
}

export type BulkAddTemplateOutput = {
  drivers: string[]
  startDate: Dayjs
  endDate: Dayjs
  templateId: string
}

const RequiredFormItem = (props: FormItemProps) => (
  <Form.Item {...props} required rules={[{ required: true }, ...(props.rules ?? [])]} />
)

type Instance = { form: FormInstance<BulkAddTemplateForm> }
const useSelectedTemplate = ({ form }: Instance, fieldToReset?: NamePath<BulkAddTemplateForm>) => {
  const templateId = Form.useWatch('templateId', { form })
  const { useDriverTemplates } = useDriverManagement()
  const { data, isLoading } = useDriverTemplates()
  const selectedTemplate = useMemo(() => data?.find((x) => x.id === templateId), [data, templateId])

  // some templates have a specific start day (of the week), and each template may have a different length,
  // meaning each selection of the template may produce different valid dates. the simplest solution is to
  // just clear the date input every time a template is selected.
  // biome-ignore lint/correctness/useExhaustiveDependencies: we specifically want to run this every time selectedTemplateId changes
  useEffect(() => {
    if (fieldToReset !== undefined) {
      form.setFieldValue(fieldToReset, null)
    }
  }, [templateId, fieldToReset, form.setFieldValue])

  return {
    selectedTemplate,
    isLoading,
  }
}

const StartDate = ({ form }: Instance) => {
  const { selectedTemplate, isLoading } = useSelectedTemplate({ form }, 'startDate')
  return (
    <RequiredFormItem name='startDate' style={{ flex: 1 }} label='Start Date'>
      <DatePicker
        style={{ width: '100%' }}
        disabled={!selectedTemplate || isLoading}
        disabledDate={(date) => selectedTemplate?.days?.length === 7 && date.day() !== selectedTemplate?.offset_day}
      />
    </RequiredFormItem>
  )
}

const isValidEndDay = (day: Dayjs, start?: Dayjs | null, length?: number) => {
  return isDefined(start) && !!length && day.isAfter(start) && day.add(1, 'day').diff(start, 'day') % length === 0
}

const EndDate = ({ form }: Instance) => {
  const { selectedTemplate, isLoading } = useSelectedTemplate({ form }, 'endDate')
  const startDate = Form.useWatch('startDate', { form })
  // biome-ignore lint/correctness/useExhaustiveDependencies: side-effect of start date changing
  useEffect(() => {
    form.setFieldValue('endDate', null)
  }, [startDate, form.setFieldValue])
  return (
    <RequiredFormItem name='endDate' style={{ flex: 1 }} label='Until'>
      <DatePicker
        style={{ width: '100%' }}
        disabled={!selectedTemplate || !startDate || isLoading}
        disabledDate={(date) => !isValidEndDay(date, startDate, selectedTemplate?.days?.length)}
      />
    </RequiredFormItem>
  )
}
const ConnectedPreview = (form: Instance) => {
  const { selectedTemplate } = useSelectedTemplate(form)
  if (!selectedTemplate) return null
  return <TemplatePreview value={selectedTemplate.days.map((x) => x.shift)} />
}
//todo: once the backend supports properly generating pattern templates (non-7day templates),
//we can flip this to true (and likely remove the mapping from endDate -> weeks_out in
//the api call, just use endDate directly)
const SUPPORTS_PATTERN_TEMPLATES = false

const Template = ({ form }: Instance) => {
  const { useDriverTemplates } = useDriverManagement()
  const { data, isLoading } = useDriverTemplates()
  const options = useMemo(
    () =>
      data
        ?.filter((x) => SUPPORTS_PATTERN_TEMPLATES || x.days.length === 7)
        ?.map((x) => ({ value: x.id ?? '', label: x.name })),
    [data]
  )
  return (
    <RequiredFormItem
      name='templateId'
      label='Template'
      help={
        <div className='mt-1'>
          <ConnectedPreview form={form} />
        </div>
      }
    >
      <Select showSearch options={options} loading={isLoading} optionFilterProp='label' placeholder='Select Template' />
    </RequiredFormItem>
  )
}

const Driver = () => <DriversSelect name='drivers' />

export const BulkAddTemplateInputs = {
  Driver,
  Template,
  StartDate,
  EndDate,
}
